import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, Star, Sparkles, Trophy } from 'lucide-react';

interface GradeRevealAnimationProps {
  score: number;
  maxScore: number;
  onComplete?: () => void;
  onClose?: () => void;
}

export function GradeRevealAnimation({ score, maxScore, onComplete, onClose }: GradeRevealAnimationProps) {
  const [stage, setStage] = useState<'grading' | 'revealing' | 'complete'>('grading');
  const percentage = Math.round((score / maxScore) * 100);
  const isHighScore = percentage >= 90;

  useEffect(() => {
    // Simulate grading delay
    const gradingTimer = setTimeout(() => {
      setStage('revealing');
    }, 1500);

    const revealTimer = setTimeout(() => {
      setStage('complete');
      onComplete?.();
      if (onClose) {
        setTimeout(() => onClose(), 2000);
      }
    }, 4000);

    return () => {
      clearTimeout(gradingTimer);
      clearTimeout(revealTimer);
    };
  }, [onComplete, onClose]);

  const getScoreColor = () => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 70) return 'text-orange-600';
    if (percentage >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getMessage = () => {
    if (percentage >= 90) return 'Excellent Work!';
    if (percentage >= 80) return 'Great Job!';
    if (percentage >= 70) return 'Good Effort!';
    if (percentage >= 60) return 'Keep Practicing!';
    return 'Keep Trying!';
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <AnimatePresence mode="wait">
        {stage === 'grading' && (
          <motion.div
            key="grading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="bg-card border border-border rounded-2xl p-12 text-center shadow-2xl"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 mx-auto mb-4"
            >
              <Sparkles className="w-16 h-16 text-primary" />
            </motion.div>
            <p className="text-xl font-semibold text-foreground">Grading...</p>
            <p className="text-sm text-muted-foreground mt-2">Please wait</p>
          </motion.div>
        )}

        {stage === 'revealing' && (
          <motion.div
            key="revealing"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            className="bg-card border border-border rounded-2xl p-12 text-center shadow-2xl relative overflow-hidden"
          >
            {isHighScore && (
              <>
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ y: 0, opacity: 1 }}
                    animate={{
                      y: -300,
                      opacity: 0,
                      x: Math.random() * 400 - 200,
                    }}
                    transition={{
                      duration: 2,
                      delay: Math.random() * 0.5,
                      ease: 'easeOut',
                    }}
                    className="absolute top-1/2 left-1/2"
                    style={{
                      color: ['#22C55E', '#6366F1', '#F59E0B', '#EF4444'][Math.floor(Math.random() * 4)],
                    }}
                  >
                    <Star className="w-6 h-6" fill="currentColor" />
                  </motion.div>
                ))}
              </>
            )}

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              {isHighScore && (
                <Trophy className="w-20 h-20 text-amber-500 mx-auto mb-4" />
              )}
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl font-bold text-foreground mb-2"
            >
              {getMessage()}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, type: 'spring' }}
              className={`text-7xl font-bold ${getScoreColor()} mb-2`}
            >
              {percentage}%
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-lg text-muted-foreground"
            >
              {score} out of {maxScore} correct
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function PendingGradeNotification({ activityName }: { activityName: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="fixed bottom-6 right-6 bg-card border border-border rounded-2xl p-6 shadow-2xl max-w-sm z-50"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles className="w-6 h-6 text-orange-600" />
          </motion.div>
        </div>
        <div>
          <p className="font-semibold text-foreground mb-1">Submission Received!</p>
          <p className="text-sm text-muted-foreground mb-2">
            Your {activityName} has been submitted for teacher review.
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-orange-100 text-orange-700 text-xs font-semibold">
            <motion.div
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ⏳
            </motion.div>
            Awaiting Teacher Review
          </div>
        </div>
      </div>
    </motion.div>
  );
}